// @flow
import styled from '@emotion/styled';
import { useState } from 'react';
import { SPACING } from '@tablekit/theme';
import Button from '@tablekit/button';
import { Link as StyledLink } from '@tablekit/typography';
import GatsbyLink from 'components/GatsbyLink';
import type { PageContextType } from 'components/PageContext';
import { getGeolocation } from 'utils/useCountryDetection';
import {
  acceptPrivacyPrompt,
  deferredLoad,
  PRIVACY_ACCEPT_KEY,
  EU_MEMBERS,
  declinePrivacyPrompt
} from './scripts';

type PropsType = {
  pageContext: PageContextType
};

const PrivacyConsentPrompt = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(6px);
`;
const ConsentText = styled.div`
  margin: ${SPACING.L4};
  color: white;
`;
const ConsentButtons = styled.div`
  display: flex;
  margin-bottom: ${SPACING.L4};
`;
const ConsentButton = styled(Button)`
  margin: 0 ${SPACING.L4} !important;
`;
const ConsentLink = styled(StyledLink.withComponent(GatsbyLink))`
  margin-left: ${SPACING.L1};
  text-decoration: none;
`;

const PrivacySensitivePrompt = ({
  pageContext: { rootSlug = '/', layout }
}: PropsType) => {
  const [showPrivacyPrompt, setShowPrivacyPrompt] = useState(false);

  const loadPrivacySensitiveScripts = () => {
    acceptPrivacyPrompt();
    deferredLoad();
  };

  if (typeof window !== 'undefined') {
    const hasAccepted = window.localStorage.getItem(PRIVACY_ACCEPT_KEY);
    const isBot =
      typeof navigator !== 'undefined' &&
      /bot|googlebot|crawler|spider|robot|crawling|lighthouse/i.test(
        navigator.userAgent
      );
    if (!hasAccepted && !isBot) {
      getGeolocation()
        .then((detectedCountry) => {
          if (
            detectedCountry &&
            EU_MEMBERS.indexOf((detectedCountry || '').toUpperCase()) >= 0
          ) {
            setShowPrivacyPrompt(true);
          } else {
            loadPrivacySensitiveScripts();
          }
        })
        .catch((e) => console.error(e));
    } else if (hasAccepted === 'true') {
      window.tsPageInteracted = true;
      loadPrivacySensitiveScripts();
    }
  }

  if (!showPrivacyPrompt) return null;
  return (
    <PrivacyConsentPrompt>
      <ConsentText>
        {layout.privacy_prompt_message}{' '}
        <ConsentLink to={`${rootSlug || '/en'}/privacy/`}>
          {layout.privacy_prompt_learn_more}
        </ConsentLink>
      </ConsentText>
      <ConsentButtons>
        <ConsentButton
          appearance="primary"
          onClick={() => {
            window.tsPageInteracted = true;
            setShowPrivacyPrompt(false);
            loadPrivacySensitiveScripts();
          }}
        >
          {layout.privacy_prompt_accept}
        </ConsentButton>
        <ConsentButton
          appearance="inverse"
          onClick={() => {
            setShowPrivacyPrompt(false);
            declinePrivacyPrompt();
          }}
        >
          {layout.privacy_prompt_decline}
        </ConsentButton>
      </ConsentButtons>
    </PrivacyConsentPrompt>
  );
};

export default PrivacySensitivePrompt;
